import { ApplicationRef, ComponentRef, EnvironmentInjector, TemplateRef, createComponent } from '@angular/core';
import { FloatingPopoverComponent } from './floating-popover.component';

export class FloatingPopoverHelper {
  private static readonly HIDE_DELAY_MS = 180;
  private popoverRef: ComponentRef<FloatingPopoverComponent> | null = null;
  private popoverHost: HTMLElement | null = null;
  private hoverBridge: HTMLElement | null = null;
  private hideTimeout: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private readonly appRef: ApplicationRef,
    private readonly envInjector: EnvironmentInjector
  ) {}

  show(
    anchor: HTMLElement,
    template: TemplateRef<unknown> | null,
    context: Record<string, unknown> | null = null
  ): void {
    if (!template) return;

    this.clearHideTimeout();

    if (this.popoverRef && this.popoverHost) {
      this.positionPopover(anchor, this.popoverHost);
      this.updateHoverBridge(anchor, this.popoverHost);
      return;
    }

    const hostElement = document.createElement('div');
    hostElement.className = 'floating-popover-host';

    const popoverRef = createComponent(FloatingPopoverComponent, {
      environmentInjector: this.envInjector,
      hostElement
    });

    popoverRef.setInput('contentTemplate', template);
    popoverRef.setInput('contentContext', context || {});

    this.appRef.attachView(popoverRef.hostView);
    popoverRef.changeDetectorRef.detectChanges();

    hostElement.addEventListener('mouseenter', () => this.clearHideTimeout());
    hostElement.addEventListener('mouseleave', () => this.scheduleHide());

    hostElement.style.visibility = 'hidden';
    hostElement.style.pointerEvents = 'none';
    document.body.appendChild(hostElement);

    this.popoverRef = popoverRef;
    this.popoverHost = hostElement;

    requestAnimationFrame(() => {
      if (!this.popoverHost || !document.body.contains(hostElement)) return;

      requestAnimationFrame(() => {
        if (!this.popoverHost || !document.body.contains(hostElement)) return;

        this.positionPopover(anchor, hostElement);
        this.updateHoverBridge(anchor, hostElement);
        hostElement.style.visibility = 'visible';
        hostElement.style.pointerEvents = 'auto';
      });
    });
  }

  scheduleHide(): void {
    this.clearHideTimeout();
    this.hideTimeout = setTimeout(() => this.remove(), FloatingPopoverHelper.HIDE_DELAY_MS);
  }

  remove(): void {
    this.clearHideTimeout();

    if (this.popoverRef) {
      this.appRef.detachView(this.popoverRef.hostView);
      this.popoverRef.destroy();
    }

    if (this.hoverBridge?.parentNode) {
      this.hoverBridge.parentNode.removeChild(this.hoverBridge);
    }

    this.popoverRef = null;
    this.popoverHost = null;
    this.hoverBridge = null;
  }

  containsTarget(target: Node | null): boolean {
    if (!target) return false;
    return (
      (!!this.popoverHost && this.popoverHost.contains(target)) ||
      (!!this.hoverBridge && this.hoverBridge.contains(target))
    );
  }

  private clearHideTimeout(): void {
    if (!this.hideTimeout) return;
    clearTimeout(this.hideTimeout);
    this.hideTimeout = null;
  }

  private positionPopover(anchor: HTMLElement, popover: HTMLElement): void {
    const anchorRect = anchor.getBoundingClientRect();
    const popoverRect = popover.getBoundingClientRect();
    const spacing = 4;
    const viewportPadding = 8;
    const top = Math.max(viewportPadding, anchorRect.top - popoverRect.height - spacing);
    const preferredLeft = anchorRect.left + anchorRect.width / 2 - popoverRect.width / 2;
    const maxLeft = window.innerWidth - popoverRect.width - viewportPadding;
    const left = Math.min(Math.max(viewportPadding, preferredLeft), Math.max(viewportPadding, maxLeft));

    popover.style.position = 'fixed';
    popover.style.top = `${top}px`;
    popover.style.left = `${left}px`;
    popover.style.zIndex = '10010';
  }

  private updateHoverBridge(anchor: HTMLElement, popover: HTMLElement): void {
    const anchorRect = anchor.getBoundingClientRect();
    const popoverRect = popover.getBoundingClientRect();
    const gapTop = Math.min(anchorRect.bottom, popoverRect.bottom);
    const gapBottom = Math.max(anchorRect.top, popoverRect.top);
    const bridgeHeight = gapBottom - gapTop;

    if (bridgeHeight <= 0) {
      this.removeHoverBridge();
      return;
    }

    const horizontalPadding = 12;
    const left = Math.max(0, Math.min(anchorRect.left, popoverRect.left) - horizontalPadding);
    const right = Math.min(
      window.innerWidth,
      Math.max(anchorRect.right, popoverRect.right) + horizontalPadding
    );
    const width = Math.max(0, right - left);

    if (!width) {
      this.removeHoverBridge();
      return;
    }

    const bridge = this.hoverBridge ?? document.createElement('div');
    bridge.className = 'floating-popover-hover-bridge';
    bridge.style.position = 'fixed';
    bridge.style.top = `${gapTop}px`;
    bridge.style.left = `${left}px`;
    bridge.style.width = `${width}px`;
    bridge.style.height = `${bridgeHeight}px`;
    bridge.style.background = 'transparent';
    bridge.style.pointerEvents = 'auto';
    bridge.style.zIndex = '10009';

    if (!this.hoverBridge) {
      bridge.addEventListener('mouseenter', () => this.clearHideTimeout());
      bridge.addEventListener('mouseleave', () => this.scheduleHide());
      document.body.appendChild(bridge);
      this.hoverBridge = bridge;
    }
  }

  private removeHoverBridge(): void {
    if (this.hoverBridge?.parentNode) {
      this.hoverBridge.parentNode.removeChild(this.hoverBridge);
    }

    this.hoverBridge = null;
  }
}
